import React from 'react';
import { ApiClient } from '@locus-taxy/encore-ui-network';
import type {
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { useLogoutWithO11y } from '../../hooks/useLogoutWithO11y';

type EncoreRequestConfig = InternalAxiosRequestConfig & {
  requireAuth?: boolean;
};

const InterceptorInstaller = ({
  baseUrl,
  getAccessTokenSilently,
  onBehalfOf,
}: {
  baseUrl: string;
  getAccessTokenSilently: () => Promise<string>;
  onBehalfOf: string;
}) => {
  const logout = useLogoutWithO11y();
  // Install request interceptor (v3 ApiClient.addRequestInterceptor) to fetch a fresh Auth0 token per request in browser
  React.useEffect(() => {
    const client = ApiClient.getInstance(baseUrl);
    // Use the new v3 interceptor API
    const interceptorId = client.addRequestInterceptor(
      async (config: EncoreRequestConfig) => {
        try {
          // Always set On-Behalf-Of header
          config.headers = {
            ...config.headers,
            'On-Behalf-Of': onBehalfOf,
          } as unknown as AxiosRequestHeaders;

          const requireAuth = config.requireAuth !== false;
          if (requireAuth) {
            const freshToken = await getAccessTokenSilently();
            if (freshToken) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${freshToken}`,
              } as unknown as AxiosRequestHeaders;
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching access token:', error);
        }
        return config;
      }
    );

    return () => {
      try {
        client.ejectRequestInterceptor(interceptorId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error ejecting request interceptor:', error);
      }
    };
  }, [baseUrl, getAccessTokenSilently, onBehalfOf]);

  // Install response interceptor (v3 ApiClient.addResponseInterceptor) to handle 401
  // This happens after token expiry post refresh-token time threshold
  // We need to log the user out if this happens
  React.useEffect(() => {
    const client = ApiClient.getInstance(baseUrl);
    const interceptorId = client.addResponseInterceptor(
      undefined,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      try {
        client.ejectResponseInterceptor(interceptorId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error ejecting response interceptor:', error);
      }
    };
  }, [baseUrl, logout]);

  // Nothing to render, just install the interceptors
  return null;
};

export default InterceptorInstaller;
